export default function fauxSelects(exclusions = ['.paginate-bar']) {
  // Select all native select elements
  const selects = document.querySelectorAll('select');

  // Guard clause: exit if no select elements are found
  if (selects.length === 0) {
    return;
  }

  selects.forEach((select, index) => {
    // Guard clause: skip if select is already wrapped in a faux-select
    if (select.closest('.faux-select')) {
      return;
    }

    // Guard clause: skip if select is within any of the excluded selectors
    const isExcluded = exclusions.some(exclusionSelector => {
      return select.closest(exclusionSelector);
    });
    
    if (isExcluded) {
      return;
    }

    // Create container for faux select
    const wrapper = document.createElement('div');
    wrapper.className = 'faux-select';
    wrapper.setAttribute('data-select-id', `faux-select-${index}`);

    // Create display element for selected option
    const display = document.createElement('div');
    display.className = 'faux-select__display';
    display.tabIndex = 0;
    display.setAttribute('role', 'combobox');
    display.setAttribute('aria-haspopup', 'listbox');
    display.setAttribute('aria-expanded', 'false');
    display.textContent = select.options[select.selectedIndex]?.text || 'Select an option';

    // Create options list
    const optionsList = document.createElement('ul');
    optionsList.className = 'faux-select__options';
    optionsList.setAttribute('role', 'listbox');
    optionsList.setAttribute('aria-hidden', 'true');

    // Populate options
    Array.from(select.options).forEach((option, optionIndex) => {
      const li = document.createElement('li');
      li.className = 'faux-select__option';
      li.setAttribute('data-value', option.value);
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', select.selectedIndex === optionIndex);
      li.textContent = option.text;
      li.tabIndex = -1;

      // Handle option selection
      li.addEventListener('click', () => {
        // Update native select value and selected attribute
        select.value = option.value;
        Array.from(select.options).forEach(opt => opt.removeAttribute('selected'));
        option.setAttribute('selected', 'selected');
        display.textContent = option.text;
        optionsList.setAttribute('aria-hidden', 'true');
        display.setAttribute('aria-expanded', 'false');
        optionsList.querySelectorAll('li').forEach(item => item.setAttribute('aria-selected', 'false'));
        li.setAttribute('aria-selected', 'true');
        select.dispatchEvent(new Event('change', { bubbles: true }));
        display.focus();
      });

      optionsList.appendChild(li);
    });

    // Sync faux select with native select changes
    select.addEventListener('change', () => {
      const selectedOption = select.options[select.selectedIndex];
      display.textContent = selectedOption?.text || 'Select an option';
      optionsList.querySelectorAll('li').forEach(item => {
        item.setAttribute('aria-selected', item.getAttribute('data-value') === select.value);
      });
    });

    // Toggle options visibility
    display.addEventListener('click', () => {
      const isHidden = optionsList.getAttribute('aria-hidden') === 'true';
      optionsList.setAttribute('aria-hidden', !isHidden);
      display.setAttribute('aria-expanded', !isHidden);
      if (!isHidden) {
        display.focus();
      } else {
        optionsList.querySelector('li').focus();
        positionOptionsList(wrapper, display, optionsList);
      }
    });

    // Close options when clicking outside
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        optionsList.setAttribute('aria-hidden', 'true');
        display.setAttribute('aria-expanded', 'false');
      }
    });

    // Keyboard accessibility for display
    display.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        optionsList.setAttribute('aria-hidden', 'false');
        display.setAttribute('aria-expanded', 'true');
        optionsList.querySelector('li').focus();
        positionOptionsList(wrapper, display, optionsList);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        optionsList.setAttribute('aria-hidden', 'false');
        display.setAttribute('aria-expanded', 'true');
        optionsList.querySelector('li:last-child').focus();
        positionOptionsList(wrapper, display, optionsList);
      } else if (e.key === 'Tab' && !optionsList.getAttribute('aria-hidden') === 'true') {
        e.preventDefault();
        optionsList.querySelector('li').focus();
        positionOptionsList(wrapper, display, optionsList);
      }
    });

    // Keyboard accessibility for options
    optionsList.addEventListener('keydown', (e) => {
      const currentOption = document.activeElement;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextOption = currentOption.nextElementSibling || optionsList.firstElementChild;
        nextOption.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevOption = currentOption.previousElementSibling || optionsList.lastElementChild;
        prevOption.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        currentOption.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        optionsList.setAttribute('aria-hidden', 'true');
        display.setAttribute('aria-expanded', 'false');
        display.focus();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        optionsList.setAttribute('aria-hidden', 'true');
        display.setAttribute('aria-expanded', 'false');
        display.focus();
      }
    });

    // Helper: Position options list above if not enough space below the select within the closest form
    function positionOptionsList(wrapper, display, optionsList) {
      // Reset to default (below)
      optionsList.classList.remove('faux-select__options--above');
      optionsList.style.bottom = '';
      optionsList.style.top = '';

      if (optionsList.getAttribute('aria-hidden') === 'true') return;
      const rect = display.getBoundingClientRect();
      const optionsHeight = optionsList.offsetHeight;
      // Find the closest form ancestor
      const form = display.closest('form');
      let formBottom = window.innerHeight;
      if (form) {
        const formRect = form.getBoundingClientRect();
        formBottom = formRect.bottom;
      }
      const spaceBelow = formBottom - rect.bottom;
      const spaceAbove = rect.top - (form ? form.getBoundingClientRect().top : 0);
      if (spaceBelow < optionsHeight && spaceAbove > optionsHeight) {
        // Not enough space below, but enough above: show above
        optionsList.classList.add('faux-select__options--above');
        optionsList.style.bottom = `${display.offsetHeight}px`;
        optionsList.style.top = 'auto';
      } else {
        // Default: show below
        optionsList.classList.remove('faux-select__options--above');
        optionsList.style.top = `${display.offsetHeight}px`;
        optionsList.style.bottom = 'auto';
      }
    }

    // Also reposition on window resize/scroll if open
    window.addEventListener('resize', () => {
      if (optionsList.getAttribute('aria-hidden') === 'false') {
        positionOptionsList(wrapper, display, optionsList);
      }
    });
    window.addEventListener('scroll', () => {
      if (optionsList.getAttribute('aria-hidden') === 'false') {
        positionOptionsList(wrapper, display, optionsList);
      }
    }, true);

    // Hide original select but keep it in DOM for form submission
    select.style.display = 'none';

    // Assemble faux select
    wrapper.appendChild(display);
    wrapper.appendChild(optionsList);
    select.parentNode.insertBefore(wrapper, select);
    wrapper.appendChild(select);
  });  
}

// Automatically observe for new <select> elements and apply fauxSelects
if (typeof window !== 'undefined' && typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutationsList) => {
    let shouldUpdate = false;
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (node.tagName === 'SELECT' || node.querySelector?.('select'))) {
            shouldUpdate = true;
          }
        });
      }
    }
    if (shouldUpdate) {
      fauxSelects();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}